import { create } from 'zustand';
import { toast } from 'react-toastify';
import { WebSocketService } from '../services/websocket';

export interface Notification {
  id: string;
  type: string;
  category?: string;
  title?: string;
  body?: string;
  data?: Record<string, unknown>;
  read: boolean;
  timestamp: number;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  connected: boolean;
  connect: (token: string) => void;
  disconnect: () => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
}

let wsService: WebSocketService | null = null;

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  connected: false,

  connect: () => {
    // Already connected — skip to avoid disconnect/reconnect cycle
    if (wsService?.connected) {
      return;
    }

    if (wsService) {
      wsService.disconnect();
    }

    wsService = new WebSocketService('/ws/notifications/', () => localStorage.getItem('token'));

    wsService.on('connection_established', () => {
      set({ connected: true });
    });

    wsService.on('notification', (data) => {
      const notification: Notification = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type: 'notification',
        category: data.category as string,
        title: data.title as string,
        body: data.body as string,
        data: data.data as Record<string, unknown>,
        read: false,
        timestamp: Date.now(),
      };
      set((state) => ({
        notifications: [notification, ...state.notifications].slice(0, 100),
        unreadCount: state.unreadCount + 1,
      }));

      // Show toast for parent messages
      if (data.category === 'parent_message') {
        toast.info(`${data.title}: ${data.body}`, { autoClose: 8000 });
      }
    });

    wsService.on('student_message', (data) => {
      const student = data.student as Record<string, unknown>;
      const notification: Notification = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type: 'student_message',
        title: 'Nouveau message élève',
        body: `${student.first_name} ${student.last_name} a envoyé un message`,
        data: data,
        read: false,
        timestamp: Date.now(),
      };
      set((state) => ({
        notifications: [notification, ...state.notifications].slice(0, 100),
        unreadCount: state.unreadCount + 1,
      }));
    });

    wsService.on('chat_message', (data) => {
      const msg = data.message as Record<string, unknown>;
      const notification: Notification = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type: 'chat_message',
        category: 'chat',
        title: 'Nouveau message',
        body: (msg.content as string)?.slice(0, 100),
        data: msg,
        read: false,
        timestamp: Date.now(),
      };
      set((state) => ({
        notifications: [notification, ...state.notifications].slice(0, 100),
        unreadCount: state.unreadCount + 1,
      }));
    });

    wsService.connect();
  },

  disconnect: () => {
    if (wsService) {
      wsService.disconnect();
      wsService = null;
    }
    set({ connected: false });
  },

  markAsRead: (id: string) => {
    set((state) => {
      const notifications = state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n,
      );
      return {
        notifications,
        unreadCount: notifications.filter((n) => !n.read).length,
      };
    });
  },

  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }));
  },

  clearAll: () => {
    set({ notifications: [], unreadCount: 0 });
  },
}));
